import React, { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Edit3, Copy, ExternalLink, Save, X, Youtube, Check } from "lucide-react";
import { api } from "../../lib/api";
import { makeSlug } from "../../lib/slug";

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ fontSize: 12, fontWeight: 700, color: "#1A1352", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
      {label}
    </label>
    {children}
  </div>
);

const VideosTab = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Create form
  const [name, setName] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [slugOverride, setSlugOverride] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState("");

  // Edit modal
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editUrl, setEditUrl] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const [copied, setCopied] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/videos");
      setVideos(data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo cargar la lista");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const effectiveSlug = slugOverride ? makeSlug(slugOverride) : makeSlug(name);
  const publicBase = window.location.origin;

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError("");
    if (!name.trim() || !youtubeUrl.trim()) {
      setCreateError("Completa nombre y URL");
      return;
    }
    if (!effectiveSlug) {
      setCreateError("El nombre no es válido para generar URL");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/admin/videos", {
        name: name.trim(),
        youtube_url: youtubeUrl.trim(),
        slug: slugOverride || undefined,
      });
      setName("");
      setYoutubeUrl("");
      setSlugOverride("");
      refresh();
    } catch (e) {
      setCreateError(e?.response?.data?.detail || "Error creando video");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm(`¿Eliminar el video "${slug}"? Esta acción es permanente.`)) return;
    try {
      await api.delete(`/admin/videos/${encodeURIComponent(slug)}`);
      refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  const openEdit = (v) => {
    setEditing(v);
    setEditName(v.name);
    setEditUrl(v.youtube_url);
  };

  const handleEditSave = async () => {
    if (!editing) return;
    setEditSaving(true);
    try {
      await api.put(`/admin/videos/${encodeURIComponent(editing.slug)}`, {
        name: editName.trim(),
        youtube_url: editUrl.trim(),
      });
      setEditing(null);
      refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setEditSaving(false);
    }
  };

  const copyLink = (slug) => {
    const url = `${publicBase}/v/${slug}`;
    navigator.clipboard?.writeText(url);
    setCopied(slug);
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div>
      <h2 className="h-display" style={{ fontSize: 32, margin: "0 0 8px", color: "#1A1352" }}>
        Páginas de video para estudiantes
      </h2>
      <p style={{ color: "#5F5A72", fontSize: 14, marginBottom: 28 }}>
        Crea enlaces permanentes que puedes compartir con cada alumno. Los videos se incrustan desde
        YouTube (recomendado en modo "No listado") y la página queda oculta para Google.
      </p>

      {/* Create form */}
      <div
        style={{
          background: "#fff",
          borderRadius: 20,
          padding: 24,
          boxShadow: "0 20px 50px -25px rgba(26,19,82,0.18)",
          border: "1px solid rgba(26,19,82,0.06)",
          marginBottom: 28,
        }}
      >
        <h3 style={{ margin: "0 0 14px", fontSize: 18, color: "#1A1352", display: "flex", alignItems: "center", gap: 8 }}>
          <Plus size={18} color="#FF4F1A" /> Nuevo video
        </h3>
        <form onSubmit={handleCreate}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Field label="Nombre del video">
              <input
                className="ae-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej. Welcome first step"
              />
            </Field>
            <Field label="URL de YouTube (no listado)">
              <input
                className="ae-input"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=XXXXXXXXXXX"
              />
            </Field>
          </div>

          <Field label="URL personalizada (opcional - se genera automáticamente)">
            <input
              className="ae-input"
              value={slugOverride}
              onChange={(e) => setSlugOverride(e.target.value)}
              placeholder={effectiveSlug || "welcomefirststep"}
            />
          </Field>

          {effectiveSlug && (
            <div
              style={{
                fontSize: 13,
                color: "#5F5A72",
                background: "#F4F1EA",
                padding: "10px 14px",
                borderRadius: 10,
                marginBottom: 14,
                wordBreak: "break-all",
              }}
            >
              Enlace público: <strong style={{ color: "#1A1352" }}>{publicBase}/v/{effectiveSlug}</strong>
            </div>
          )}

          {createError && <div style={{ color: "#E83E0A", fontSize: 13, marginBottom: 12 }}>{createError}</div>}
          <button type="submit" className="btn-orange" disabled={submitting}>
            {submitting ? "Creando…" : (<><Plus size={15} /> Crear página</>)}
          </button>
        </form>
      </div>

      {/* List */}
      <h3 style={{ margin: "0 0 14px", fontSize: 18, color: "#1A1352" }}>
        Videos creados ({videos.length})
      </h3>

      {loading && <div style={{ color: "#5F5A72" }}>Cargando…</div>}
      {error && <div style={{ color: "#E83E0A" }}>{error}</div>}

      {!loading && videos.length === 0 && (
        <div style={{ background: "#fff", borderRadius: 18, padding: 30, color: "#5F5A72", textAlign: "center", border: "1px dashed rgba(26,19,82,0.15)" }}>
          Aún no has creado ningún video. Usa el formulario de arriba.
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
        {videos.map((v) => {
          const url = `${publicBase}/v/${v.slug}`;
          const thumb = v.youtube_id ? `https://img.youtube.com/vi/${v.youtube_id}/mqdefault.jpg` : null;
          return (
            <div
              key={v.slug}
              style={{
                background: "#fff",
                borderRadius: 18,
                overflow: "hidden",
                boxShadow: "0 10px 30px -16px rgba(26,19,82,0.18)",
                border: "1px solid rgba(26,19,82,0.06)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {thumb && (
                <div style={{ aspectRatio: "16/9", background: "#000" }}>
                  <img src={thumb} alt={v.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
              )}
              <div style={{ padding: 16, flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                <h4 style={{ margin: 0, fontSize: 16, color: "#1A1352", fontWeight: 700 }}>{v.name}</h4>
                <div style={{ fontSize: 12, color: "#5F5A72", wordBreak: "break-all" }}>
                  <span style={{ color: "#FF4F1A", fontWeight: 600 }}>/v/{v.slug}</span>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: "auto", paddingTop: 8 }}>
                  <button onClick={() => copyLink(v.slug)} className="btn-ghost" style={{ fontSize: 12 }}>
                    {copied === v.slug ? <Check size={12} /> : <Copy size={12} />}
                    {copied === v.slug ? "Copiado" : "Copiar"}
                  </button>
                  <a href={url} target="_blank" rel="noopener noreferrer" className="btn-ghost" style={{ fontSize: 12 }}>
                    <ExternalLink size={12} /> Abrir
                  </a>
                  <button onClick={() => openEdit(v)} className="btn-ghost" style={{ fontSize: 12 }}>
                    <Edit3 size={12} /> Editar
                  </button>
                  <button
                    onClick={() => handleDelete(v.slug)}
                    className="btn-ghost"
                    style={{ fontSize: 12, color: "#E83E0A" }}
                  >
                    <Trash2 size={12} /> Borrar
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit modal */}
      {editing && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(26,19,82,0.5)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            zIndex: 100,
          }}
          onClick={() => setEditing(null)}
        >
          <div
            style={{ background: "#fff", borderRadius: 20, padding: 28, maxWidth: 540, width: "100%", boxShadow: "0 30px 70px -20px rgba(0,0,0,0.4)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ margin: 0, color: "#1A1352", fontSize: 20 }}>Editar video</h3>
              <button onClick={() => setEditing(null)} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#5F5A72" }}>
                <X size={20} />
              </button>
            </div>
            <div
              style={{
                fontSize: 12,
                color: "#5F5A72",
                background: "#F4F1EA",
                padding: "8px 12px",
                borderRadius: 8,
                marginBottom: 14,
              }}
            >
              URL pública: <strong style={{ color: "#FF4F1A" }}>/v/{editing.slug}</strong>{" "}
              <span style={{ opacity: 0.7 }}>(no cambia)</span>
            </div>
            <Field label="Nombre">
              <input className="ae-input" value={editName} onChange={(e) => setEditName(e.target.value)} />
            </Field>
            <Field label="URL de YouTube">
              <input className="ae-input" value={editUrl} onChange={(e) => setEditUrl(e.target.value)} />
            </Field>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setEditing(null)} className="btn-ghost">
                Cancelar
              </button>
              <button onClick={handleEditSave} className="btn-orange" disabled={editSaving}>
                {editSaving ? "Guardando…" : (<><Save size={15} /> Guardar</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideosTab;
